/**
 * Resolves a value from linkedFields or returns literal value
 * Supports ${variable} syntax for dynamic value resolution
 * @param {string} value - The value to resolve
 * @param {object} linkedFields - The linkedFields object to resolve from
 * @returns {any} - The resolved value
 */
function resolveValue(value, linkedFields) {
  if (!value) return value;
  if (typeof value !== 'string' || !value.includes('${')) return value;

  // Replace ALL ${variable} occurrences; return null if any key is missing
  let hasUnresolved = false;
  const resolved = value.replace(/\$\{(\w+)\}/g, (_, key) => {
    const val = linkedFields[key];
    if (val === null || val === undefined || val === '') {
      hasUnresolved = true;
      return '';
    }
    return val;
  });

  return hasUnresolved ? null : resolved;
}

function buildQueryString(entityParams, linkedFields) {

  if (!entityParams) return '';

  // Handle new filter-based approach
  if (entityParams.type === 'filter' && entityParams.filters) {
    const filterParts = [];

    for (const filter of entityParams.filters) {
      const { key, value } = filter;

      if (!key || value === undefined) continue;

      // Resolve the value (supports ${variable} syntax)
      const resolvedValue = resolveValue(value, linkedFields || {});

      // Skip if resolved value is null, undefined, or empty string
      if (resolvedValue === null || resolvedValue === undefined || resolvedValue === '') {
        continue;
      }

      // Format as key:value for the filter parameter
      filterParts.push(`${key}:${resolvedValue}`);
    }

    if (filterParts.length === 0) return '';

    // Join all filters with semicolons and create the filter parameter
    const filterValue = filterParts.join(';');
    const query = `filter=${encodeURIComponent(filterValue)}`;
    return `?${query}`;
  }

  // Handle 'query' mode: individual params with ${variable} resolution → ?key=value&key=value
  if (entityParams.type === 'query' && entityParams.params) {
    const parts = [];
    for (const param of entityParams.params) {
      const { key, value } = param;
      if (!key || value === undefined) continue;
      const resolvedValue = resolveValue(value, linkedFields || {});
      if (resolvedValue === null || resolvedValue === undefined || resolvedValue === '') continue;
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(resolvedValue)}`);
    }
    if (parts.length === 0) return '';
    return `?${parts.join('&')}`;
  }

  // Handle legacy direct field mapping approach (backward compatibility)
  if (entityParams.type === 'direct' || (!entityParams.type && entityParams.fields)) {
    const fields = entityParams.fields;
    const name = entityParams.name;
    
    if (!fields || !name) return '';
    
    const values = fields
      .map(field => linkedFields ? linkedFields[field] : undefined)
      .filter(value => value !== undefined && value !== null && value !== '');

    if (values.length === 0) return '';

    const query = values
      .map(value => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
      .join('&');

    return `?${query}`;
  }

  // Handle plain flat object: { key: value, ... } → ?key=value&...
  const params = Object.entries(entityParams)
    .filter(([_, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return params ? `?${params}` : '';
}

export default buildQueryString
